"use client";
import React, { useEffect, useState } from "react";
import Link from "next/link";
import {
  MdOutlineVisibilityOff,
  MdOutlineVisibility,
  MdOutlineAddCircleOutline,
} from "react-icons/md";
import CryptoJS from "crypto-js";
import { usePathname, useRouter } from "next/navigation";
import moment from "moment/moment";
import "moment/locale/fr";
import "moment/locale/ar";
import Image from "next/image";
import Logo from "@/public/logo.png";
import ecommerceIllustration from "@/public/ecommerce-illustration.png";
import { UploadSpot } from "@/components/elements/FileUpload";
import AutocompleteComponent from "@/components/elements/AutocompleteComponent";

import { TiDeleteOutline } from "react-icons/ti";
import { IconButton } from "@mui/material";

import { CustomTelInput } from "@/components/elements/CustomTelInput";
import { useTranslation } from "react-i18next";
import { signIn, useSession } from "next-auth/react";
import { UserSession } from "@/utils/types";
import { useDrawer } from "@/utils/DrawerProvider";
import Terms from "@/components/drawers/Terms&Conditions";
import { Checkbox } from "@mui/material";
import api from "@/app/lib/api";
import { toast } from "react-toastify";
import { LoginDialog } from "@/components/dialogs/LoginDialog";
import { useDialog } from "@/utils/DialogProvider";
import path from "path";

export default function SignUpDrawer({ drawerid }: { drawerid?: string }) {
  const router = useRouter();
  const session = useSession().data as UserSession;
  const pathname = usePathname();
  const { t, i18n } = useTranslation();
  const currentLocale = i18n.language as "ar" | "fr" | "en";
  moment.locale(currentLocale);
  const { closeDialog, openDialog } = useDialog();
  const { closeDrawer, openDrawer } = useDrawer();

  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone_numbers: [""],
    profile_pic: "",
    password: "",
    permissions: [] as any,
    role: "user" as "user" | "admin" | "super-admin",
    adress: "",
    country: "Algérie",
    agreed_to_terms: false,
  });

  const [passwordConfirmation, setPasswordConfirmation] = useState({
    password: "",
    isPasswordVisible: false,
  });

  const [error, setError] = useState("");
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handlePhoneNumberChange = (index: number, value: string) => {
    const updatedPhoneNumbers = [...formData.phone_numbers];
    updatedPhoneNumbers[index] = value;
    setFormData({ ...formData, phone_numbers: updatedPhoneNumbers });
  };

  const addPhoneNumber = () => {
    setFormData({
      ...formData,
      phone_numbers: [...formData.phone_numbers, ""],
    });
  };

  const deletePhoneNumber = (index: number) => {
    const updatedPhoneNumbers = [...formData.phone_numbers];
    updatedPhoneNumbers.splice(index, 1);
    setFormData({ ...formData, phone_numbers: updatedPhoneNumbers });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError("");
    const isEmpty = formData.phone_numbers[0] == "" || formData.country == "";
    if (isEmpty) {
      setError(t("requiredField"));
      console.log("empty field");
      return { error: true };
    }

    if (formData.password != passwordConfirmation.password) {
      setError(t("incorrectPassword"));
      return { error: true };
    }
    if (!formData.agreed_to_terms) {
      setError(t("Please agree to our terms and conditions to sign up"));
      return { error: true };
    }
    const hashedPassword = CryptoJS.SHA256(formData.password).toString();
    const user = {
      ...formData,
      profile_pic: `${formData.profile_pic}`,
      password: hashedPassword,
    };

    try {
      await api.users.insert(user);
      toast.success(t("registrationSuccess"), { position: "top-center" });
      await signIn("credentials", {
        email: formData.email.replace(/\s/g, ""),
        password: formData.password,
      });
      return { success: true };
    } catch (error: any) {
      toast.error(`${t("error")} : ${t([error?.message ?? "error"])}`, {
        position: "top-center",
      });
      return { error: true };
    }
  };

  useEffect(() => {
    if (pathname.includes("signup") && session?.role == "user") {
      router.push("/");
    }
    if (!pathname.includes("signup") && session?.role == "user") {
      closeDrawer(drawerid ?? "");
    }
  }, [session]);

  return (
    <form onSubmit={handleSubmit}>
      <div
        className={
          pathname.includes("signup")
            ? " c2 mc1 gap50 p50   mp0"
            : " c1 mc1 gap50    mp0"
        }
      >
        {pathname.includes("signup") && <SignUpBanner />}

        <div className="auth-form">
          <div className="grid gap10">
            <div>
              <h2 className={"fw800"}>{t("createAccount")} </h2>
              <p className={" secondary-color"}>{t("signupContinue")}</p>
            </div>
            <div>
              <label className={"custom-file-upload"}>{t("profilePic")} </label>
              <UploadSpot
                value={formData.profile_pic}
                onChange={(newFile) => {
                  setFormData({ ...formData, profile_pic: newFile?.url || "" });
                }}
              />
            </div>
            <div className="c2 gap10 mc1">
              <div>
                <label htmlFor="">{t("firstName")} *</label>
                <input
                  className=" grey-bg"
                  type="text"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleChange}
                  required
                />
              </div>

              <div>
                <label htmlFor="">{t("lastName")} *</label>
                <input
                  className=" grey-bg"
                  type="text"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>
            <div>
              <label htmlFor="">{t("email")} *</label>
              <input
                className=" grey-bg"
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
              />
              {formData.email && error == t("emailAlreadyUsed") && (
                <div className="red f13">{error}</div>
              )}
            </div>{" "}
            <div>
              <div className="c1-auto vcenter">
                <label htmlFor="">{t("phoneNumber")} *</label>
                <IconButton size="small" type="button" onClick={addPhoneNumber}>
                  <MdOutlineAddCircleOutline className="blue" />
                </IconButton>
              </div>

              <div className="grid gap10">
                {formData.phone_numbers.map((number, index) => (
                  <div key={index} className="c1-auto gap5 vcenterg">
                    <CustomTelInput
                      number={number}
                      onNumberChanged={(n: string) =>
                        handlePhoneNumberChange(index, n)
                      }
                    />
                    {index > 0 && (
                      <IconButton
                        size="small"
                        type="button"
                        onClick={() => deletePhoneNumber(index)}
                      >
                        <TiDeleteOutline className="red" />
                      </IconButton>
                    )}
                  </div>
                ))}
              </div>

              {formData.phone_numbers[0] == "" && error != "" && (
                <div className="red f13">{error}</div>
              )}
            </div>
            <div>
              <label htmlFor="">{t("address")} *</label>
              <input
                className=" grey-bg"
                type="text"
                name="adress"
                value={formData.adress}
                onChange={handleChange}
                required
              />
            </div>
            <div>
              <label htmlFor="">{t("country")} *</label>
              <AutocompleteComponent
                defaultValue={{
                  id: 3,
                  name: { en: "Algeria", fr: "Algérie", ar: "الجزائر" },
                }}
                is_multiple={false}
                onChange={(newselectedItem: any) => {
                  setFormData({ ...formData, country: newselectedItem?.name });
                }}
                itemType={"countries"}
              />
            </div>
            <div>
              <label htmlFor=""> {t("passwordLabel")} * </label>
              <div className="relative">
                <input
                  className=" grey-bg"
                  type={isPasswordVisible ? "text" : "password"}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                />
                <span
                  className="password-toggle"
                  style={{
                    right: currentLocale == "ar" ? "" : "10px",
                    left: currentLocale == "ar" ? "10px" : "",
                  }}
                  onClick={() => setIsPasswordVisible(!isPasswordVisible)}
                >
                  {isPasswordVisible ? (
                    <MdOutlineVisibilityOff />
                  ) : (
                    <MdOutlineVisibility />
                  )}
                </span>
              </div>
            </div>
            <div>
              <label htmlFor=""> {t("confirmPasswordLabel")} *</label>
              <div className="relative">
                <input
                  className=" grey-bg"
                  type={
                    passwordConfirmation.isPasswordVisible ? "text" : "password"
                  }
                  id="confirm-password"
                  name="confirm-password"
                  value={passwordConfirmation.password}
                  onChange={(e) =>
                    setPasswordConfirmation({
                      ...passwordConfirmation,
                      password: e.target.value,
                    })
                  }
                  required
                />
                <span
                  className="password-toggle"
                  style={{
                    right: currentLocale == "ar" ? "" : "10px",
                    left: currentLocale == "ar" ? "10px" : "",
                  }}
                  onClick={() =>
                    setPasswordConfirmation({
                      ...passwordConfirmation,
                      isPasswordVisible: !isPasswordVisible,
                    })
                  }
                >
                  {passwordConfirmation.isPasswordVisible ? (
                    <MdOutlineVisibilityOff />
                  ) : (
                    <MdOutlineVisibility />
                  )}
                </span>
              </div>

              {error != "" &&
                error != t("requiredField") &&
                error != t("requiredField") &&
                error !=
                  t("Please agree to our terms and conditions to sign up") &&
                error != t("incorrectPassword") &&
                error != t("emailAlreadyUsed") &&
                error != t("incorrectEmail") && (
                  <div className="red f13">{error}</div>
                )}
              {passwordConfirmation.password != "" &&
                passwordConfirmation.password != formData.password &&
                error == t("incorrectPassword") && (
                  <div className="red f13">{error}</div>
                )}
            </div>
            <div>
              <div className="auto-c1 vcenter gap10">
                <div className="auto-c1 vcenter gap10">
                  <div className="relative">
                    <input
                      type="checkbox"
                      name="agreed_to_terms"
                      id="agreed_to_terms"
                      className="checkbox"
                      checked={formData.agreed_to_terms}
                      onChange={() => {
                        setFormData({
                          ...formData,
                          agreed_to_terms: !formData.agreed_to_terms,
                        });
                      }}
                    />
                  </div>
                  <div className="flex gap5" style={{ flexWrap: "wrap" }}>
                    <p>{t("agreedTo")} </p>
                    <div
                      onClick={() =>
                        openDrawer({ title: "", content: <Terms /> })
                      }
                      className="pointer"
                    >
                      <p className="light-blue">{t("terms")} </p>
                    </div>
                  </div>
                </div>
              </div>
              {!formData.agreed_to_terms &&
                error ==
                  t("Please agree to our terms and conditions to sign up") && (
                  <div className="red f13">{error}</div>
                )}
            </div>
            <button type="submit" className="primary-button mt20">
              {" "}
              {t("createAccount")}{" "}
            </button>
            {pathname.includes("signup") ? (
              <Link href="/login" className="tcenter">
                <h3 className="dark-grey">{t("login")} </h3>
              </Link>
            ) : (
              <div
                className="pointer"
                onClick={() => {
                  closeDialog();
                  closeDrawer(drawerid ?? "");
                  openDrawer({
                    id: "login",
                    content: <LoginDialog />,
                    title: t("login"),
                  });
                }}
              >
                <h3 className="dark-grey">{t("login")} </h3>
              </div>
            )}
          </div>
          <br />
        </div>
      </div>
    </form>
  );
}

const SignUpBanner = () => {
  const { t, i18n } = useTranslation();
  const currentLocale = i18n.language as "ar" | "fr" | "en";
  return (
    <div className="grid gap30 web-only h100vh">
      <Link href={"/"} className="grid center tcenter w100px">
        <Image
          src={Logo}
          alt=""
          className="h80px pointer"
          width={80}
          height={80}
        />
        <p className="f13"> Amazigh Import </p>
      </Link>
      <h1>{t("discoverInfinitePossibilities")}</h1>
      <Image src={ecommerceIllustration} alt="" className="h250px w100" />
    </div>
  );
};
